import { useState, useEffect, useRef } from 'react';
import { SERVICES } from '../../mocks/lisaMerkleData';
import { Link } from 'react-router-dom';

interface ServicesSectionProps {
  title?: string;
  subtitle?: string;
  showAll?: boolean;
  limit?: number;
}

const SERVICE_ICONS_BG = [
  'ri-home-4-line',
  'ri-tools-line',
  'ri-hotel-bed-line',
  'ri-restaurant-2-line',
];

export default function ServicesSection({
  title = 'Our Services',
  subtitle = 'Expert Interior Design & Luxury Remodeling Services',
  showAll = false,
  limit = 12,
}: ServicesSectionProps) {
  const [expanded, setExpanded] = useState(showAll);
  const displayed = expanded ? SERVICES : SERVICES.slice(0, limit);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        el.querySelectorAll('.reveal').forEach((node, i) => {
          setTimeout(() => node.classList.add('revealed'), i * 50);
        });
        obs.disconnect();
      }
    }, { threshold: 0.05 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section ref={ref} className="bg-white py-28 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-8">

        {/* Header with side image */}
        <div className="reveal grid lg:grid-cols-[1fr_auto] gap-10 items-end mb-16">
          <div>
            <span className="text-[10px] tracking-[0.28em] uppercase text-stone font-sans">{title}</span>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light text-charcoal mt-3 leading-tight">
              {subtitle}
            </h2>
            <p className="text-charcoal/50 text-sm leading-relaxed font-sans mt-5 max-w-lg">
              A comprehensive suite of luxury design services — from full home transformations to curated single-room refinements — all delivered with uncompromising craftsmanship.
            </p>
          </div>
          <div className="hidden lg:flex flex-col items-end gap-4">
            <Link
              to="/services"
              className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase font-sans text-stone-dark border-b border-stone/30 pb-0.5 hover:border-stone-dark hover:gap-4 transition-all cursor-pointer whitespace-nowrap"
            >
              View All 24 Services <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>

        {/* Feature banner with image + highlight */}
        <div className="reveal grid lg:grid-cols-5 gap-0 mb-0.5 h-56 overflow-hidden">
          <div className="lg:col-span-2 relative overflow-hidden">
            <img
              src="https://public.readdy.ai/ai/img_res/54dc3bdfd432c29e36bbdb5f513ff55b.jpg"
              alt="Lisa Merkle Design luxury interior services"
              className="w-full h-full object-cover object-top transition-transform duration-1000 hover:scale-105"
            />
            <div className="absolute inset-0 bg-charcoal/30" />
            <div className="absolute inset-0 flex items-end p-8">
              <p className="font-display text-white text-xl font-light leading-snug italic">
                &ldquo;Design that<br />elevates everyday life.&rdquo;
              </p>
            </div>
          </div>
          <div className="lg:col-span-3 grid grid-cols-3">
            {SERVICE_ICONS_BG.slice(0, 3).map((_, idx) => {
              const featureServices = [
                { label: '24 Services', sub: 'From full home to fine details', icon: 'ri-layout-grid-line' },
                { label: 'Beverly Hills', sub: 'Studio-led personal service', icon: 'ri-map-pin-line' },
                { label: 'Full-Service', sub: 'Concept through installation', icon: 'ri-check-double-line' },
              ];
              const item = featureServices[idx];
              return (
                <div key={item.label} className={`flex flex-col justify-center p-7 bg-cream-warm border-r border-stone/15 last:border-r-0 ${idx === 1 ? 'bg-charcoal' : ''}`}>
                  <div className="w-9 h-9 flex items-center justify-center mb-4">
                    <i className={`${item.icon} text-xl ${idx === 1 ? 'text-stone' : 'text-stone-dark'}`} />
                  </div>
                  <p className={`font-display text-xl font-medium leading-none mb-1 ${idx === 1 ? 'text-cream' : 'text-charcoal'}`}>{item.label}</p>
                  <p className={`text-[11px] font-sans mt-1.5 leading-snug ${idx === 1 ? 'text-cream/50' : 'text-charcoal/45'}`}>{item.sub}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Services Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-0.5 bg-stone/10 mt-0.5">
          {displayed.map((service, i) => (
            <Link
              key={service.title}
              to="/services"
              className={`reveal bg-white p-8 hover:bg-[#F5F0E8] transition-all duration-400 group cursor-pointer border-0 ${i < 4 ? `reveal-delay-${i + 1}` : ''}`}
            >
              {/* Icon circle */}
              <div className="flex items-start justify-between mb-5">
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-stone/8 group-hover:bg-stone/15 transition-colors duration-300">
                  <i className={`${service.icon} text-stone-dark text-lg`} />
                </div>
                <span className="font-display text-3xl font-light text-stone/12 group-hover:text-stone/22 transition-colors leading-none">
                  {String(i + 1).padStart(2, '0')}
                </span>
              </div>

              {/* Thin accent line */}
              <div className="w-5 h-px bg-stone/30 group-hover:w-10 group-hover:bg-stone-dark/50 transition-all duration-500 mb-4" />

              <h3 className="font-sans font-semibold text-charcoal text-sm mb-2.5 group-hover:text-stone-dark transition-colors leading-snug">
                {service.title}
              </h3>
              <p className="text-charcoal/50 text-xs leading-relaxed font-sans font-light">{service.description}</p>
              
              {/* Arrow on hover */}
              <div className="flex items-center gap-1.5 mt-5 opacity-0 group-hover:opacity-100 translate-x-[-4px] group-hover:translate-x-0 transition-all duration-300">
                <span className="text-[9px] tracking-wider uppercase font-sans text-stone-dark">Inquire</span>
                <i className="ri-arrow-right-line text-[10px] text-stone-dark" />
              </div>
            </Link>
          ))}
        </div>

        {!showAll && SERVICES.length > limit && (
          <div className="text-center mt-10">
            <button
              onClick={() => setExpanded(!expanded)}
              className="px-10 py-3.5 border border-charcoal/20 text-charcoal text-[10px] tracking-[0.2em] uppercase font-sans hover:bg-charcoal hover:text-cream transition-all duration-300 cursor-pointer whitespace-nowrap rounded-full"
            >
              {expanded ? 'Show Less' : `View All ${SERVICES.length} Services`}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}